import React, { useEffect, useState, useContext } from 'react';
import { saveAs } from 'file-saver';
import moment from 'moment';
import { Col, Row, Tabs, Tab } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BsFileText } from 'react-icons/bs';
import { ImSpinner4 } from 'react-icons/im';
import Page from '../../components/page';
import Unit from '../../components/single-unit';
import UnitSkeleton from '../../components/unit-skeleton';
import { api } from '../../providers/apiClient';
import Breadcrumbs from '../../components/layout/breadcrumbs';
import { ContextUser } from '../../providers/ContextUser';
import { ContextSearch } from '../../providers/ContextSearch';
import InfoData from '../../components/info-data';
import Search from '../../components/search';
import NoResults from '../../components/no-result';
import {
  generateReportName,
  removeExtentionFileName,
  toTitleCase,
  applyTextFilters,
  hasActiveFilters,
} from '../../utils';
import {
  CustomSelect,
  DocumentContainer,
  HorizontalSplit,
  Subtitle,
  UnitsContainer,
  NotificationQuantity,
  CustomTabTitle,
  ExportActionButton,
} from './styles';

moment();

const PageBranch = ({ isCollapsed }) => {
  const { handleUpdateCurrentOrganization } = useContext(ContextUser);
  const { searchValues, setSearchValues } = useContext(ContextSearch);
  const [units, setUnits] = useState([]);
  const [searchUnitResult, setSearchUnitResult] = useState([]);
  const [branch, setBranch] = useState();
  const [isFetching, setIsFetching] = useState(true);
  const [breadcrumbs, setBreadcrumbs] = useState([]);
  const [fetchingConsolidatedReport, setFetchingConsolidatedReport] =
    useState(false);
  const { branchId } = useParams();
  const [documents, setDocuments] = useState([]);
  const [documentSelected, setDocumentSelected] = useState('');
  const [tab, setTab] = useState('units');
  const [notifications, setNotifications] = useState([]);
  const [notificationsLength, setNotificationsLength] = useState(0);

  // Carrega dados apenas uma vez na montagem do componente
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsFetching(true);
        
        // Carrega dados da API
        const response = await api.get(`/branchs/${branchId}/units`);
        const data = response.data;
        
        // Atualiza estados
        setUnits(data.units);
        setBranch(data);
        setNotifications(data.notifications);
        setDocuments(data.documents || []);
        setBreadcrumbs(data.breadcrumbs);
        handleUpdateCurrentOrganization(data.organization);
        setNotificationsLength(
          data.notifications.filter((notification) =>
            [1, 2].includes(notification.status.id)
          ).length
        );
        
        // Aplica filtros salvos após carregar dados
        applyFiltersLocally(data.units, searchValues['branch'] || {});
        
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setIsFetching(false);
      }
    };

    loadData();
  }, []); // Sem dependências - executa apenas uma vez

  // Aplica filtros localmente quando searchValues mudar
  useEffect(() => {
    if (units.length > 0) {
      if (hasActiveFilters(searchValues, 'branch')) {
        applyFiltersLocally(units, searchValues['branch']);
      } else {
        // Se não há filtros ativos, mostra todas as unidades
        setSearchUnitResult(units);
      }
    }
  }, [searchValues, units]);

  const handleExportComplianceReport = async () => {
    setFetchingConsolidatedReport(true);
    toast.success('Gerando relatório, aguarde.');

    api
      .get(`/branchs/${branchId}/report`, {
        responseType: 'blob',
      })
      .then((response) => {
        const file = new Blob([response.data], { type: 'application/pdf' });
        
        // Usar o nome do arquivo definido no backend (Content-Disposition header)
        const contentDisposition = response.headers['content-disposition'];
        const fileName = contentDisposition 
          ? contentDisposition.split('filename=')[1] 
          : generateReportName('compliance', branch.organization.name, branch.name);

        saveAs(file, fileName);
      })
      .catch((error) => {
        console.log(error);
      })
      .finally(() => setFetchingConsolidatedReport(false));
  };

  const ButtonExportCompliance = (
    <ExportActionButton
      id='branch-export-compliance-btn'
      key='btnExportCompliance'
      onClick={handleExportComplianceReport}
      type='button'
      disabled={fetchingConsolidatedReport || units.length <= 0}
      className='action-button info'
    >
      {fetchingConsolidatedReport ? (
        <>
          <ImSpinner4 style={{ animation: 'spin 2s infinite linear' }} />
          Aguarde
        </>
      ) : (
        <>
          <BsFileText />
          Relatório de Pendências LGPD
        </>
      )}
    </ExportActionButton>
  );

  const handleFileSelection = (event) => {
    setDocumentSelected(event.target.value);

    if (!event.target.value) {
      setDocumentSelected('');
      return;
    }

    const link = document.createElement('a');
    link.href = event.target.value;
    link.setAttribute('download', '');
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };



  // Função para aplicar filtros localmente sem chamar API
  const applyFiltersLocally = (allUnits, filters) => {
    const searchFields = ['name', 'responsible.name', 'responsible.email'];
    applyTextFilters(allUnits, filters, searchFields, setSearchUnitResult);
  };

  const filteredDocs = documents.filter((doc) =>
    doc.name?.includes(' - (DPO)')
  );

  return (
    <>
      <Breadcrumbs id='breadcrumbs-branch' path={breadcrumbs} />
      <Page
        id='page-branch'
        title={branch && branch.name}
        sideAction={[ButtonExportCompliance]}
        icon={branch && branch.iconUrl}
      >
        {documents.length > 0 && (
          <DocumentContainer id='branch-documents'>
            <Subtitle id='branch-documents-title'>Documentos</Subtitle>
            <CustomSelect
              id='branch-documents-select'
              value={documentSelected}
              onChange={handleFileSelection}
            >
              <option defaultValue='' value='' disabled selected>
                Selecione um documento para download
              </option>
              {filteredDocs.length > 0 && (
                <>
                  {filteredDocs.map((doc) => (
                    <option value={doc.url} key={doc.url}>
                      {toTitleCase(removeExtentionFileName(doc.name)).replace(
                        / - \(dpo\)/i,
                        ' - (DPO)'
                      )}
                    </option>
                  ))}
                  <option defaultValue='' value='' disabled selected>
                    -------------------------------------------------
                  </option>
                </>
              )}
              {documents
                .filter((doc) => !doc.name?.includes(' - (DPO)'))
                .map((doc) => (
                  <option value={doc.url} key={doc.url}>
                    {toTitleCase(removeExtentionFileName(doc.name))}
                  </option>
                ))}
            </CustomSelect>
          </DocumentContainer>
        )}
        <InfoData
          compliance={branch?.compliance}
          riskMatrix={branch?.riskMatrix}
        />
        <HorizontalSplit />
        <Tabs
          id='branch-tabs'
          activeKey={tab}
          onSelect={(currentTab) => setTab(currentTab)}
          className='mb-3'
        >
          <Tab eventKey='units' title='Unidades'>
            <UnitsContainer id='branch-units-container'>
              <Search
                id='search-branch'
                data={units}
                fields={['name', 'responsible.name', 'responsible.email']}
                placeholder='Busque por unidade ou dados do responsável...'
                setSearchResult={setSearchUnitResult}
                onSearchTextChange={setSearchValues}
                pageName='branch'
              />
              <div style={{ display: 'block', marginTop: '20px' }}>
                <Row id='branch-units-grid' style={{ width: '100%' }} className="g-3">
                  {isFetching ? (
                    [0, 1, 2, 3, 4, 5].map((item) => (
                      <Col id={`skeleton-unit-${item}`} md={isCollapsed ? 4 : 6} key={`skeleton-${item}`}>
                        <UnitSkeleton id={`skeleton-unit-${item}`} />
                      </Col>
                    ))
                  ) : searchUnitResult.length > 0 ? (
                  searchUnitResult.map((item) => (
                    <Col id={`unit-card-${item.id}`} md={isCollapsed ? 4 : 6} key={item.id}>
                      <Unit item={item} />
                    </Col>
                  ))
                  ) : (
                    <NoResults
                      id='no-results-branch'
                      message='Nenhuma unidade encrontrada'
                      searchText={searchValues['branch']?.text || ''}
                    />
                  )}
                </Row>
              </div>
            </UnitsContainer>
          </Tab>
        </Tabs>
      </Page>
    </>
  );
};

export default PageBranch;
